import { useCallback, useMemo } from "react";
import { useQuery, type CombinedError } from "urql";
import { graphql } from "@acto/gql";
import { type GeoPrecision, type GetNearbyRankedClientsQuery, KpiComparisonMode } from "@acto/gql/graphql";
import { Customer } from "@acto/taskpane/core/domain";
import { mapClientToCustomer } from "@acto/taskpane/core/data-access/customers/mapClientToCustomer";
import { yearToDateRange } from "@acto/web-common/dates";

const NearbyRankedClientsQuery = graphql(`
  query GetNearbyRankedClients($clientId: ID!, $dateRange: DateRangeInput!, $comparisonMode: KpiComparisonMode!) {
    nearbyRankedClients(clientId: $clientId) {
      __typename
      originPrecision
      suggestions {
        __typename
        client {
          ...ClientForDetailsWithRecommendedObservation
          revenue(dateRange: $dateRange, comparisonMode: $comparisonMode) {
            __typename
            totalValue
            previousTotalValue
            percentageChange
            dateRanges {
              __typename
              current {
                __typename
                startDate
                endDate
              }
            }
          }
          profit(dateRange: $dateRange, comparisonMode: $comparisonMode) {
            __typename
            totalValue
            previousTotalValue
            percentageChange
          }
        }
        proximity {
          __typename
          minutesMin
          minutesMax
          distanceKmMin
          distanceKmMax
          precision
        }
      }
    }
  }
`);

type NearbySuggestionNode = GetNearbyRankedClientsQuery["nearbyRankedClients"]["suggestions"][number];

export type ProximityValue = Omit<NonNullable<NearbySuggestionNode["proximity"]>, "__typename">;

export interface NearbySuggestionItem {
  customer: Customer;
  proximity: ProximityValue | null;
}

export interface NearbyRankedClientsResult {
  /** Wire order — the server owns ranking and filtering. */
  suggestions?: NearbySuggestionItem[];
  revenuePeriod: { start: Date; end: Date } | null;
  originPrecision?: GeoPrecision | null;
  isPending: boolean;
  error?: CombinedError;
  refetch: () => void;
}

export function useNearbyRankedClients(clientId: string | null): NearbyRankedClientsResult {
  const dateRange = yearToDateRange();
  // Year to date compares against last year whatever the tenant configured.
  const comparisonMode = KpiComparisonMode.YearOverYear;
  const [result, reexecuteQuery] = useQuery({
    query: NearbyRankedClientsQuery,
    variables: { clientId: clientId ?? "", dateRange, comparisonMode },
    pause: !clientId,
    requestPolicy: "cache-and-network",
  });

  const suggestions = useMemo(
    () =>
      result.data?.nearbyRankedClients.suggestions.map((suggestion) => ({
        customer: mapClientToCustomer(suggestion.client),
        proximity: suggestion.proximity ?? null,
      })),
    [result.data],
  );

  const revenuePeriod = useMemo(() => {
    const firstWithRange = result.data?.nearbyRankedClients.suggestions.find(
      (s) => s.client.revenue?.dateRanges?.current != null,
    );
    const current = firstWithRange?.client.revenue?.dateRanges?.current;
    if (!current?.startDate || !current?.endDate) return null;
    return {
      start: new Date(current.startDate),
      end: new Date(current.endDate),
    };
  }, [result.data]);

  const refetch = useCallback(() => reexecuteQuery({ requestPolicy: "network-only" }), [reexecuteQuery]);

  return {
    suggestions,
    revenuePeriod,
    originPrecision: result.data?.nearbyRankedClients.originPrecision,
    isPending: result.fetching || result.stale,
    error: result.error,
    refetch,
  };
}
